import { useState } from "react";
import { WORLDS, TIER_WORLDS } from "../../data/worlds";
import { PARENT_MILESTONES } from "../../lib/storage";
import { REWARD_ITEMS, THEME_GRADIENTS, type RewardCategory } from "../../data/rewardItems";
import { getThisWeeksMissions } from "../../data/missions";

export default function ProgressScreen({ progress, xp, coins, streak, tier, parentData, onParentPortal, onLeaderboard, rewardsData, onSaveRewards, equippedAvatarEmoji, missionsData, onCompleteMission, onSubmitForVerify }: any) {
  const [showStore, setShowStore] = useState(false);
  const [storeTab, setStoreTab]   = useState<RewardCategory>("avatar");

  const tierWorldIds = TIER_WORLDS[tier ?? "starter"] ?? [];
  const tierWorlds   = WORLDS.filter(w => tierWorldIds.includes(w.id));
  const tierTotal    = tierWorlds.reduce((s, w) => s + w.lessons.length, 0);
  const done         = tierWorlds.reduce((s, w) => s + w.lessons.filter((_, i) => progress[`${w.id}-${i}`]).length, 0);
  const level   = Math.floor(xp / 500) + 1;
  const levelXp = xp % 500;

  const weeklyMissions  = getThisWeeksMissions();
  const completed: string[]    = missionsData?.completed    ?? [];
  const pendingVerify: string[] = missionsData?.pendingVerify ?? [];

  const unlockedItems: string[] = rewardsData?.unlockedItems ?? [];
  const equipped = rewardsData?.equipped ?? { avatar: null, title: null, theme: null };
  const avatarEmoji = equippedAvatarEmoji ?? "🦸";

  const badges = [
    { e: "🌱", n: "First Step",  desc: "Complete 1 lesson",    u: done >= 1            },
    { e: "📚", n: "Bookworm",    desc: "Complete 5 lessons",    u: done >= 5            },
    { e: "🔥", n: "On Fire",     desc: "3-day streak",          u: streak >= 3          },
    { e: "💰", n: "Money Wise",  desc: "Complete 10 lessons",   u: done >= 10           },
    { e: "🧠", n: "Genius",      desc: "Earn 1000 XP",          u: xp >= 1000           },
    { e: "🏆", n: "Champion",    desc: "All lessons done!",     u: tierTotal > 0 && done >= tierTotal },
  ];

  const parentBadges = PARENT_MILESTONES.map(m => ({
    e: m.emoji, n: m.label, desc: m.desc, u: !!(parentData?.milestones?.[m.id]),
  }));

  function buyItem(itemId: string, cost: number) {
    if (!rewardsData || coins < cost) return;
    const next = { ...rewardsData, coins: rewardsData.coins - cost, unlockedItems: [...unlockedItems, itemId] };
    onSaveRewards(next);
  }

  function equipItem(category: RewardCategory, itemId: string) {
    if (!rewardsData) return;
    const isEquipped = equipped[category] === itemId;
    const next = { ...rewardsData, equipped: { ...equipped, [category]: isEquipped ? null : itemId } };
    onSaveRewards(next);
  }

  const tabItems = REWARD_ITEMS.filter(i => i.category === storeTab);

  return (
    <div style={{ fontFamily: "Nunito", padding: "16px", paddingBottom: 100 }}>
      {/* Hero card */}
      <div style={{ background: "linear-gradient(135deg,#f59e0b,#ef4444,#ec4899)", borderRadius: 22, padding: "20px 18px",
        marginBottom: 16, textAlign: "center", position: "relative", overflow: "hidden",
        boxShadow: "0 8px 28px #ef444444" }}>
        <div style={{ position: "absolute", top: -20, right: -15, fontSize: 90, opacity: 0.12 }}>🏆</div>
        <div style={{ fontSize: 54, lineHeight: 1, animation: "bounce 2s infinite" }}>{avatarEmoji}</div>
        <div style={{ fontSize: 24, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One", marginTop: 4 }}>Level {level} Money Hero!</div>
        <div style={{ background: "#ffffff35", borderRadius: 20, height: 14, margin: "12px 24px 5px", overflow: "hidden" }}>
          <div style={{ height: "100%", background: "#fff", borderRadius: 20,
            width: `${(levelXp / 500) * 100}%`, transition: "width 0.7s" }} />
        </div>
        <div style={{ fontSize: 13, color: "#ffffffcc", fontWeight: 800 }}>{levelXp}/500 XP to Level {level + 1} 🌟</div>
      </div>

      {/* Stats row */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginBottom: 12 }}>
        {[{ e: "⭐", n: "Lessons", v: done }, { e: "🪙", n: "Coins", v: coins }, { e: "🔥", n: "Streak", v: `${streak}d` }].map(s => (
          <div key={s.n} style={{ background: "#fff", borderRadius: 18, padding: "14px 8px", textAlign: "center",
            border: "2.5px solid #fef3c7", boxShadow: "0 4px 16px #0001" }}>
            <div style={{ fontSize: 28, animation: "bounce 2s infinite" }}>{s.e}</div>
            <div style={{ fontSize: 24, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>{s.v}</div>
            <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 800 }}>{s.n}</div>
          </div>
        ))}
      </div>

      {/* ── This Week's Missions ── */}
      {(() => {
        const doneCount = weeklyMissions.filter(m => completed.includes(m.id)).length;
        return (
          <div style={{ background: "#fff", borderRadius: 20, padding: "16px", border: "2.5px solid #fef3c7", marginBottom: 12 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 17, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>🎯 This Week's Missions</div>
              <div style={{ background: doneCount === 3 ? "#dcfce7" : "#fef3c7", borderRadius: 20, padding: "3px 10px", fontSize: 12, fontWeight: 900, color: doneCount === 3 ? "#15803d" : "#92400e" }}>
                {doneCount}/3 done
              </div>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {weeklyMissions.map(m => {
                const isDone    = completed.includes(m.id);
                const isPending = pendingVerify.includes(m.id);
                const typeColor = m.type === "parent" ? "#8b5cf6" : "#0ea5e9";
                const typeBg    = m.type === "parent" ? "#f5f3ff" : "#f0f9ff";

                if (isDone) return (
                  <div key={m.id} style={{ background: "#f0fdf4", borderRadius: 14, padding: "12px 14px", border: "2px solid #bbf7d0", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 28 }}>✅</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: "#15803d", fontFamily: "Fredoka One" }}>{m.title}</div>
                      <div style={{ fontSize: 11, color: "#16a34a", fontWeight: 700 }}>Completed! +{m.xp} XP earned</div>
                    </div>
                  </div>
                );

                if (isPending) return (
                  <div key={m.id} style={{ background: "#fffbeb", borderRadius: 14, padding: "12px 14px", border: "2px solid #fde68a", display: "flex", alignItems: "center", gap: 10 }}>
                    <div style={{ fontSize: 28 }}>{m.emoji}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 900, color: "#92400e", fontFamily: "Fredoka One" }}>{m.title}</div>
                      <div style={{ fontSize: 11, color: "#b45309", fontWeight: 700 }}>⏳ Waiting for parent to confirm…</div>
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 900, color: "#d97706", background: "#fef3c7", borderRadius: 8, padding: "3px 8px" }}>+{m.xp} XP</div>
                  </div>
                );

                return (
                  <div key={m.id} style={{ background: typeBg, borderRadius: 14, padding: "12px 14px", border: `2px solid ${typeColor}33` }}>
                    <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                      <div style={{ fontSize: 26, lineHeight: 1.2 }}>{m.emoji}</div>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 2 }}>
                          <div style={{ fontSize: 13, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One" }}>{m.title}</div>
                          <div style={{ fontSize: 9, fontWeight: 900, color: typeColor, background: `${typeColor}18`, borderRadius: 6, padding: "2px 6px", textTransform: "uppercase" as const }}>
                            {m.type === "parent" ? "Parent ✓" : "Self"}
                          </div>
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280", fontWeight: 700, lineHeight: 1.4 }}>{m.desc}</div>
                      </div>
                      <div style={{ fontSize: 11, fontWeight: 900, color: typeColor, background: `${typeColor}18`, borderRadius: 8, padding: "3px 8px", whiteSpace: "nowrap" as const }}>+{m.xp} XP</div>
                    </div>
                    <button
                      onClick={() => m.type === "self" ? onCompleteMission(m.id, m.xp) : onSubmitForVerify(m.id)}
                      style={{ width: "100%", background: typeColor, border: "none", borderRadius: 10, padding: "8px", color: "#fff", fontWeight: 900, fontSize: 12, cursor: "pointer", fontFamily: "Nunito" }}>
                      {m.type === "self" ? "I Did It! ✅" : "Done — ask parent to confirm 👨‍👩‍👧"}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* Rewards Store entry button */}
      <button onClick={() => setShowStore(true)}
        style={{ width: "100%", background: "linear-gradient(135deg,#fbbf24,#f97316)", border: "none", borderRadius: 18, padding: "14px 18px", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14, boxShadow: "0 4px 16px #f9731633" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>🛍️</span>
          <div style={{ textAlign: "left" as const }}>
            <div>Rewards Store</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#fff9" }}>Spend your coins on cool stuff</div>
          </div>
        </div>
        <div style={{ background: "#ffffff30", borderRadius: 20, padding: "4px 12px", fontSize: 14, fontWeight: 900 }}>
          🪙 {coins}
        </div>
      </button>

      {/* World progress */}
      <div style={{ background: "#fff", borderRadius: 20, padding: "16px", marginBottom: 14, border: "2.5px solid #fef3c7" }}>
        <div style={{ fontSize: 17, fontWeight: 900, color: "#1f2937", marginBottom: 12, fontFamily: "Fredoka One" }}>🗺️ World Progress</div>
        {tierWorlds.map(w => {
          const d = w.lessons.filter((_, i) => progress[`${w.id}-${i}`]).length;
          const p = d / w.lessons.length;
          return (
            <div key={w.id} style={{ marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 5 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#374151" }}>{w.emoji} {w.label}</div>
                <div style={{ fontSize: 13, fontWeight: 900, color: w.color }}>{d}/{w.lessons.length} ⭐</div>
              </div>
              <div style={{ background: "#f3f4f6", borderRadius: 20, height: 11, overflow: "hidden" }}>
                <div style={{ width: `${p * 100}%`, height: "100%",
                  background: `linear-gradient(90deg,${w.color},${w.dark})`,
                  borderRadius: 20, transition: "width 0.7s" }} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Achievements */}
      <div style={{ background: "#fff", borderRadius: 20, padding: "16px", border: "2.5px solid #fef3c7", marginBottom: 14 }}>
        <div style={{ fontSize: 17, fontWeight: 900, color: "#1f2937", marginBottom: 12, fontFamily: "Fredoka One" }}>🏅 Achievements</div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
          {badges.map(b => (
            <div key={b.n} style={{ textAlign: "center", opacity: b.u ? 1 : 0.28, transition: "opacity 0.4s" }}>
              <div style={{ fontSize: 38, lineHeight: 1,
                filter: b.u ? "drop-shadow(0 4px 10px rgba(0,0,0,0.15))" : "grayscale(1)",
                animation: b.u ? "bounce 2.5s infinite" : "none" }}>{b.e}</div>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#374151", marginTop: 5 }}>{b.n}</div>
              <div style={{ fontSize: 10, color: b.u ? "#10b981" : "#9ca3af", fontWeight: 700 }}>{b.u ? "Unlocked! 🎉" : b.desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Parent-verified real-world badges */}
      {parentBadges.some(b => b.u) && (
        <div style={{ background: "#fff", borderRadius: 20, padding: "16px", border: "2.5px solid #ede9fe", marginBottom: 14 }}>
          <div style={{ fontSize: 17, fontWeight: 900, color: "#1f2937", marginBottom: 4, fontFamily: "Fredoka One" }}>🌟 Real-World Wins</div>
          <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 700, marginBottom: 12 }}>Verified by your parent</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 12 }}>
            {parentBadges.filter(b => b.u).map(b => (
              <div key={b.n} style={{ textAlign: "center" }}>
                <div style={{ fontSize: 38, lineHeight: 1, animation: "bounce 2.5s infinite", filter: "drop-shadow(0 4px 10px rgba(0,0,0,0.15))" }}>{b.e}</div>
                <div style={{ fontSize: 11, fontWeight: 900, color: "#374151", marginTop: 5 }}>{b.n}</div>
                <div style={{ fontSize: 10, color: "#8b5cf6", fontWeight: 700 }}>Unlocked! 🎉</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Leaderboard entry */}
      <button onClick={onLeaderboard}
        style={{ width: "100%", background: "linear-gradient(135deg,#f97316,#ef4444)", border: "none", borderRadius: 18, padding: "14px 18px", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, boxShadow: "0 4px 16px #f9731633" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span style={{ fontSize: 22 }}>🏅</span>
          <div style={{ textAlign: "left" as const }}>
            <div>Global Leaderboard</div>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#fff9" }}>See how you rank worldwide</div>
          </div>
        </div>
        <span style={{ fontSize: 18 }}>→</span>
      </button>

      {/* Parent portal entry */}
      <button onClick={onParentPortal}
        style={{ width: "100%", background: "linear-gradient(135deg,#8b5cf6,#6d28d9)", border: "none", borderRadius: 18, padding: "14px 18px", color: "#fff", fontWeight: 900, fontSize: 15, cursor: "pointer", fontFamily: "Fredoka One", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, boxShadow: "0 4px 16px #8b5cf633" }}>
        <span style={{ fontSize: 20 }}>👨‍👩‍👧</span> For Parents
      </button>

      {/* ══ REWARDS STORE MODAL ══════════════════════════════════════════════ */}
      {showStore && (
        <div style={{ position: "fixed", inset: 0, background: "#00000066", zIndex: 100, display: "flex", alignItems: "flex-end", justifyContent: "center" }} onClick={() => setShowStore(false)}>
          <div style={{ background: "#fff", borderRadius: "24px 24px 0 0", padding: "24px 20px 48px", width: "100%", maxWidth: 480, maxHeight: "88vh", overflowY: "auto" }} onClick={e => e.stopPropagation()}>
            <div style={{ width: 40, height: 4, background: "#e5e7eb", borderRadius: 4, margin: "0 auto 16px" }} />

            {/* Store header */}
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
              <div>
                <div style={{ fontSize: 22, fontWeight: 900, fontFamily: "Fredoka One", color: "#1f2937" }}>🛍️ Rewards Store</div>
                <div style={{ fontSize: 13, color: "#6b7280", fontWeight: 700 }}>Spend your hard-earned coins!</div>
              </div>
              <div style={{ background: "linear-gradient(135deg,#fbbf24,#f97316)", borderRadius: 20, padding: "6px 14px", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ fontSize: 16 }}>🪙</span>
                <span style={{ fontSize: 18, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>{coins}</span>
              </div>
            </div>

            {/* Category tabs */}
            <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
              {(["avatar", "title", "theme"] as RewardCategory[]).map(tab => (
                <button key={tab} onClick={() => setStoreTab(tab)}
                  style={{ flex: 1, background: storeTab === tab ? "linear-gradient(135deg,#fbbf24,#f97316)" : "#f3f4f6", border: "none", borderRadius: 12, padding: "8px 4px", cursor: "pointer", fontFamily: "Nunito", fontWeight: 900, fontSize: 12, color: storeTab === tab ? "#fff" : "#6b7280", transition: "all 0.15s" }}>
                  {tab === "avatar" ? "🦸 Avatars" : tab === "title" ? "📛 Titles" : "🎨 Themes"}
                </button>
              ))}
            </div>

            {/* Items grid */}
            {storeTab === "theme" ? (
              // Themes: 1-column with gradient preview
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                {tabItems.map(item => {
                  const owned    = unlockedItems.includes(item.id);
                  const isEquipped = equipped.theme === item.id;
                  const canAfford = coins >= item.cost;
                  return (
                    <div key={item.id} style={{ borderRadius: 16, overflow: "hidden", border: `2.5px solid ${isEquipped ? "#f97316" : owned ? "#10b981" : "#e5e7eb"}`, boxShadow: isEquipped ? "0 4px 16px #f9731633" : "none" }}>
                      <div style={{ background: THEME_GRADIENTS[item.id] ?? THEME_GRADIENTS.default, padding: "14px 16px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <span style={{ fontSize: 26 }}>{item.emoji}</span>
                          <div>
                            <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", fontFamily: "Fredoka One" }}>{item.name}</div>
                            <div style={{ fontSize: 11, color: "#ffffff99", fontWeight: 700 }}>{item.desc}</div>
                          </div>
                        </div>
                        {isEquipped
                          ? <button onClick={() => equipItem("theme", item.id)} style={{ background: "#ffffff30", border: "none", borderRadius: 10, padding: "6px 12px", color: "#fff", fontWeight: 900, fontSize: 12, cursor: "pointer" }}>✅ On</button>
                          : owned
                            ? <button onClick={() => equipItem("theme", item.id)} style={{ background: "#ffffff30", border: "none", borderRadius: 10, padding: "6px 12px", color: "#fff", fontWeight: 900, fontSize: 12, cursor: "pointer" }}>Equip</button>
                            : <button onClick={() => buyItem(item.id, item.cost)} disabled={!canAfford}
                                style={{ background: canAfford ? "#ffffff30" : "#ffffff15", border: "none", borderRadius: 10, padding: "6px 12px", color: canAfford ? "#fff" : "#ffffff55", fontWeight: 900, fontSize: 12, cursor: canAfford ? "pointer" : "default" }}>
                                🪙 {item.cost}
                              </button>
                        }
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              // Avatars & Titles: 2-column grid
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                {tabItems.map(item => {
                  const owned      = unlockedItems.includes(item.id);
                  const isEquipped = equipped[storeTab] === item.id;
                  const canAfford  = coins >= item.cost;
                  return (
                    <div key={item.id} style={{ background: isEquipped ? "#fff7ed" : "#fff", borderRadius: 16, padding: "14px 12px", textAlign: "center", border: `2.5px solid ${isEquipped ? "#f97316" : owned ? "#10b981" : "#e5e7eb"}`, boxShadow: isEquipped ? "0 4px 16px #f9731633" : "0 2px 8px #0001" }}>
                      <div style={{ fontSize: 38, marginBottom: 6, lineHeight: 1 }}>{item.emoji}</div>
                      <div style={{ fontSize: 13, fontWeight: 900, color: "#1f2937", fontFamily: "Fredoka One", marginBottom: 2 }}>{item.name}</div>
                      <div style={{ fontSize: 10, color: "#9ca3af", fontWeight: 700, marginBottom: 10 }}>{item.desc}</div>
                      {isEquipped ? (
                        <button onClick={() => equipItem(storeTab, item.id)}
                          style={{ width: "100%", background: "#fff7ed", border: "2px solid #f97316", borderRadius: 10, padding: "6px", color: "#ea580c", fontWeight: 900, fontSize: 11, cursor: "pointer" }}>
                          ✅ Equipped
                        </button>
                      ) : owned ? (
                        <button onClick={() => equipItem(storeTab, item.id)}
                          style={{ width: "100%", background: "#f0fdf4", border: "2px solid #10b981", borderRadius: 10, padding: "6px", color: "#059669", fontWeight: 900, fontSize: 11, cursor: "pointer" }}>
                          Equip
                        </button>
                      ) : (
                        <button onClick={() => buyItem(item.id, item.cost)} disabled={!canAfford}
                          style={{ width: "100%", background: canAfford ? "linear-gradient(135deg,#fbbf24,#f97316)" : "#f3f4f6", border: "none", borderRadius: 10, padding: "6px", color: canAfford ? "#fff" : "#9ca3af", fontWeight: 900, fontSize: 11, cursor: canAfford ? "pointer" : "default" }}>
                          🪙 {item.cost}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* How to earn more coins */}
            <div style={{ background: "#fef9c3", border: "2px solid #fbbf24", borderRadius: 14, padding: "12px 14px", marginTop: 20 }}>
              <div style={{ fontSize: 12, fontWeight: 900, color: "#92400e", marginBottom: 4 }}>💡 How to earn coins</div>
              <div style={{ fontSize: 11, color: "#78350f", fontWeight: 700, lineHeight: 1.5 }}>
                ✅ Complete a lesson correctly → 20 coins<br />
                📝 Complete a lesson (any score) → 10 coins<br />
                🔥 Daily challenge correct → 10 coins<br />
                🔥 Daily challenge any answer → 5 coins
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
